import { SyntheticEvent, useState } from 'react';
import { IconName, TextInput, InputValue, Stack, BareIconButton } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { KeyValue, UnicodeEntity } from '@mews/enums';
import { Resource } from '@mews-commander/resources';
import { NavigationDirection } from '../../types';

interface ResourceSearchProps {
    onChange: (value: InputValue) => void;
    navigateHighlightedResources: (direction: NavigationDirection) => void;
    resourcesToHighlight: Resource[];
    searchedSpace: Resource | null;
    isTimelineSpaceSearchNavigationEnabled: boolean;
}

export const ResourceSearch = ({
    onChange,
    navigateHighlightedResources,
    resourcesToHighlight,
    searchedSpace,
    isTimelineSpaceSearchNavigationEnabled,
}: ResourceSearchProps) => {
    const [value, setValue] = useState<InputValue>('');

    const handleChange = (e: SyntheticEvent, changedValue: InputValue) => {
        onChange(changedValue);
        setValue(changedValue);
    };

    const handleKeyDown: React.KeyboardEventHandler = (e) => {
        if (e.key === KeyValue.Enter) {
            onChange(value);
        }
    };

    const currentHighlightedSpacePosition =
        resourcesToHighlight.findIndex((resource) => resource.id === searchedSpace?.id) + 1 || 0;

    const totalResources = resourcesToHighlight.length;

    const resourceCountElement = isTimelineSpaceSearchNavigationEnabled && value && (
        <>
            {currentHighlightedSpacePosition}
            {UnicodeEntity.ForwardSlash}
            {totalResources}
        </>
    );

    const navigationButtonsElement = isTimelineSpaceSearchNavigationEnabled && totalResources > 1 && (
        <Stack>
            <BareIconButton
                icon={IconName.ChevronUp}
                onClick={() => navigateHighlightedResources(NavigationDirection.Prev)}
                disabled={currentHighlightedSpacePosition === 1}
            />
            <BareIconButton
                icon={IconName.ChevronDown}
                onClick={() => navigateHighlightedResources(NavigationDirection.Next)}
                disabled={currentHighlightedSpacePosition === totalResources}
            />
        </Stack>
    );

    return (
        <TextInput
            value={value}
            name="resource-search"
            placeholder={<LocalizedText textKey={TextKey.SearchResource} />}
            indicativeIcon={IconName.Search}
            clearable
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            data-test-id="timeline-resource-search"
            suffix={resourceCountElement}
            trailingImage={navigationButtonsElement}
            size="medium"
        />
    );
};
